/**
 * Pure geometry helpers for hazard collision. Framework-agnostic and fully
 * deterministic (no `Math.random`, no `Date`, no engine imports) so the SAME
 * tests run in the browser (Phaser) and on the server (Cloud Function
 * re-simulation for anti-cheat) and agree byte-for-byte.
 *
 * These complement {@link import('./collision')} (which carries the simpler
 * circle-vs-circle / circle-vs-square rules used elsewhere): here we cover the
 * shapes the twelve attack patterns actually produce — axis-aligned rectangles
 * (walls), thick line segments (laser walls / missiles), and rotated beams
 * about a centre (the caro). {@link import('./world').hazardHitsPlayer} narrows
 * on hazard kind and dispatches to the matching helper below.
 *
 * Convention: the player is always a circle of centre `(px, py)` and radius `r`;
 * "overlap" is inclusive (touching counts as a hit) to match the legacy
 * pixel-perfect behaviour as closely as a geometric approximation allows.
 *
 * All helpers take plain scalars (never a {@link import('./types').Vec2}) so a
 * caller on the hot path never allocates just to run a test.
 */

/** Squared distance between two points — avoids a `Math.sqrt` on the hot path. */
function distSq(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

/**
 * Clamp `v` to the inclusive range `[lo, hi]`. (`lo <= hi` is assumed; all
 * callers here pass already-ordered bounds.)
 */
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Circle vs axis-aligned rectangle. The rectangle is given by its top-left
 * corner `(rx, ry)` and its size `(rw, rh)` — the same representation as
 * {@link import('./types').RectHazard}. Finds the rectangle point nearest the
 * circle centre and tests whether it lies within `r`.
 *
 * @param px circle centre x.
 * @param py circle centre y.
 * @param r  circle radius.
 * @param rx rectangle top-left x.
 * @param ry rectangle top-left y.
 * @param rw rectangle width  (>= 0).
 * @param rh rectangle height (>= 0).
 * @returns true if the circle touches or overlaps the rectangle.
 */
export function circleIntersectsRect(
  px: number,
  py: number,
  r: number,
  rx: number,
  ry: number,
  rw: number,
  rh: number,
): boolean {
  const nearestX = clamp(px, rx, rx + rw);
  const nearestY = clamp(py, ry, ry + rh);
  return distSq(px, py, nearestX, nearestY) <= r * r;
}

/**
 * Shortest squared distance from a point to a line segment `(x1,y1)-(x2,y2)`.
 * The classic projection-onto-segment formula, with the projection parameter
 * clamped to `[0,1]` so the nearest point stays on the segment (not its infinite
 * line). A zero-length segment degenerates to point-to-point distance.
 */
function pointSegmentDistSq(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return distSq(px, py, x1, y1);
  // Parametric projection of P onto the segment, clamped to the segment span.
  const t = clamp(((px - x1) * dx + (py - y1) * dy) / lenSq, 0, 1);
  const projX = x1 + t * dx;
  const projY = y1 + t * dy;
  return distSq(px, py, projX, projY);
}

/**
 * Circle vs thick line segment (a "capsule"): true when the player circle of
 * radius `r` overlaps a beam of half-thickness `thickness/2` running from
 * `(x1,y1)` to `(x2,y2)`. Used for laser-wall and missile hazards.
 *
 * @param px        circle centre x.
 * @param py        circle centre y.
 * @param r         circle radius.
 * @param x1,y1     segment start.
 * @param x2,y2     segment end.
 * @param thickness full beam width (px); the lethal radius is `thickness/2`.
 * @returns true on contact.
 */
export function circleIntersectsSegment(
  px: number,
  py: number,
  r: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  thickness: number,
): boolean {
  const reach = r + thickness / 2;
  return pointSegmentDistSq(px, py, x1, y1, x2, y2) <= reach * reach;
}

/**
 * Circle vs a beam expressed as a centre, an angle, and a length — the form the
 * rotating caro uses. The beam radiates OUTWARD from `(cx,cy)` along `angle`
 * (radians) for `length` px (i.e. it is a spoke, not a diameter), with full
 * width `thickness`. Internally this resolves the beam's far endpoint and
 * delegates to {@link circleIntersectsSegment}.
 *
 * @param px        circle centre x.
 * @param py        circle centre y.
 * @param r         circle radius.
 * @param cx,cy     beam pivot (hub) centre.
 * @param angle     beam direction (radians); 0 points +x, increasing CCW in math
 *                  terms / CW on screen (y-down) — the renderer uses the same.
 * @param length    spoke length from the hub (px).
 * @param thickness full beam width (px).
 * @returns true on contact.
 */
export function circleIntersectsRotatedBeam(
  px: number,
  py: number,
  r: number,
  cx: number,
  cy: number,
  angle: number,
  length: number,
  thickness: number,
): boolean {
  const ex = cx + Math.cos(angle) * length;
  const ey = cy + Math.sin(angle) * length;
  return circleIntersectsSegment(px, py, r, cx, cy, ex, ey, thickness);
}

/** Circle vs circle (centre `(ax,ay)` r `ar` against `(bx,by)` r `br`). */
export function circleIntersectsCircle(
  ax: number,
  ay: number,
  ar: number,
  bx: number,
  by: number,
  br: number,
): boolean {
  const reach = ar + br;
  return distSq(ax, ay, bx, by) <= reach * reach;
}
